import { Alert, Badge, Card, Table, Button, Modal } from 'react-bootstrap'
import { IconButton } from '../../../../components/atoms/IconButton'
import { TableTitle } from '../../../../components/atoms/TableTitle'
import { useState } from 'react'
import { useToasts } from 'react-toast-notifications'

import * as S from './styles'
export type ValuesPersonTable = {
  id: string
  name: string
  cpfOrCnpj: string
  mail: string
  status: string
  signed_at: string
  email_sent_at: string
  email_opened_at: string
  email_opened: boolean
}

export type PersonsTableProps = {
  value: ValuesPersonTable[]
  onResendEmail?: (signatureId: string) => Promise<void>
}

export const formatContractSignatureStatusVariant = {
  Assinado: 'success',
  Pendente: 'warning'
}

export const PersonsTable = ({ value, onResendEmail }: PersonsTableProps) => {
  const [showModal, setShowModal] = useState(false)
  const [selectedPerson, setSelectedPerson] =
    useState<ValuesPersonTable | null>(null)
  const [isResending, setIsResending] = useState(false)
  const { addToast } = useToasts()

  const handleResendClick = (person: ValuesPersonTable) => {
    setSelectedPerson(person)
    setShowModal(true)
  }

  const handleConfirmResend = async () => {
    if (!selectedPerson || !onResendEmail) return

    setIsResending(true)
    try {
      await onResendEmail(selectedPerson.id)
      addToast('Email de assinatura reenviado com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })
      setShowModal(false)
      setSelectedPerson(null)
    } catch (error) {
      addToast('Erro ao reenviar email. Tente novamente.', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsResending(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedPerson(null)
  }

  return (
    <S.Wrapper>
      <Card className="table-card">
        <Card.Header>
          <TableTitle>Assinantes</TableTitle>
        </Card.Header>
        <Card.Body className="pb-0">
          <S.CustomTable responsive hover className="mb-0">
            <thead>
              <tr>
                <th>NOME</th>
                <th>CPF / CNPJ</th>
                <th>E-MAIL</th>
                <th>STATUS DO EMAIL</th>
                <th>STATUS DA ASSINATURA</th>
                <th>DATA DA ASSINATURA</th>
                <th>AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {value.map((person) => (
                <tr key={person.id}>
                  <td>{person.name}</td>
                  <td>{person.cpfOrCnpj}</td>
                  <td>{person.mail}</td>
                  <td>
                    {!person.email_sent_at ? (
                      <Badge variant="secondary">Não enviado</Badge>
                    ) : person.email_opened ? (
                      <Badge variant="success">✓ Aberto</Badge>
                    ) : (
                      <Badge variant="warning">Enviado</Badge>
                    )}
                  </td>
                  <td>
                    <S.CustomBadge
                      variant={
                        formatContractSignatureStatusVariant[
                          person.status as keyof typeof formatContractSignatureStatusVariant
                        ]
                      }
                    >
                      {person.status}
                    </S.CustomBadge>
                  </td>
                  <td>{person.signed_at}</td>
                  <td>
                    {person.status === 'Pendente' && onResendEmail && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleResendClick(person)}
                        title="Reenviar email de assinatura"
                      >
                        Reenviar Email
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </S.CustomTable>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Reenvio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza que deseja reenviar o email de assinatura para{' '}
            <strong>{selectedPerson?.name}</strong>?
          </p>
          <p className="text-muted">
            O email será enviado para: {selectedPerson?.mail}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={isResending}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmResend}
            disabled={isResending}
          >
            {isResending ? 'Enviando...' : 'Confirmar Reenvio'}
          </Button>
        </Modal.Footer>
      </Modal>
    </S.Wrapper>
  )
}
